/**
 * Universal NotificationsBell.
 *
 * Feature-agnostic. Reads from /api/notifications/ which serves every
 * mentorship + networking notification through a single registry-driven
 * pipeline, so this component renders ANY notification type — current
 * or future — without code changes.
 *
 * The previous mentorship-coupled bell had a hardcoded `describe(n)`
 * switch statement per type. That's gone. The bell now renders
 * `notification.display` directly (server-rendered via the backend
 * registry) and picks an icon from `notification.icon`.
 *
 * Design notes
 * ------------
 * - Polls /api/notifications/unread_summary/ every 60s for the badge
 *   count (cheap — one GROUP BY query, no row fetching).
 * - On dropdown open, fetches the first page of /api/notifications/.
 * - Toasts when a previously unseen notification arrives (compares with
 *   the previous tick's id set, same as the prior bell).
 * - Click on a notification → mark-one-read + (if default_url) navigate.
 * - All optimistic UI mutations roll back on error.
 */

import { useEffect, useRef, useState } from 'react';
import {
    AlertCircle,
    Award,
    Bell,
    Briefcase,
    CheckCircle,
    ClipboardList,
    Inbox,
    MessageCircle,
    Shield,
    UserCheck,
    UserPlus,
    Users,
    XCircle,
} from 'lucide-react';
import {
    listNotifications,
    getUnreadSummary,
    markNotificationRead,
    markAllNotificationsRead,
    type Notification,
} from '../../../notificationsService';
import { ToastContainer, useToast } from '../../../core/ui/toast/Toast';
import './NotificationsBell.css';

const POLL_MS = 120_000;

/**
 * Static map of every icon the backend registry currently emits.
 * Keys are kebab-case (matching the lucide CDN names that the backend
 * uses); values are tree-shakable named imports above. Adding a new
 * icon to a backend NotificationType requires one entry here — much
 * cheaper than bundling all 1000+ lucide icons.
 *
 * Falls back to `Bell` when an unknown name arrives so the bell never
 * crashes on a new type the frontend hasn't yet pulled.
 */
const ICONS: Record<string, React.ComponentType<{ size?: number }>> = {
    'alert-circle': AlertCircle,
    'award': Award,
    'bell': Bell,
    'briefcase': Briefcase,
    'check-circle': CheckCircle,
    'clipboard-list': ClipboardList,
    'inbox': Inbox,
    'message-circle': MessageCircle,
    'shield': Shield,
    'user-check': UserCheck,
    'user-plus': UserPlus,
    'users': Users,
    'x-circle': XCircle,
};

function IconFor({ name, size = 16 }: { name: string; size?: number }) {
    const Component = ICONS[name] ?? Bell;
    return <Component size={size} />;
}

type Props = {
    /**
     * Optional click handler called with `notification.default_url` when
     * the user clicks a notification row. Lets the parent SPA navigate
     * via its own routing instead of forcing window.location changes.
     * If omitted, the bell just marks-as-read and does nothing else.
     */
    onNavigate?: (defaultUrl: string, notification: Notification) => void;
};

function NotificationsBell({ onNavigate }: Props) {
    const [items, setItems] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [open, setOpen] = useState(false);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    // All / Unread / Mentions filter tabs. "Mentions" filters the
    // message-category items whose payload carries a mention marker;
    // when the backend registry adds a dedicated type later, this will
    // light up without UI changes.
    const [tab, setTab] = useState<'all' | 'unread' | 'mentions'>('all');

    // ── Refs (don't trigger re-renders) ────────────────────────────────────
    // seenIds: which notifications we've already toasted about
    const seenIds = useRef<Set<string>>(new Set());
    // isFirstLoad: skip toasts on the very first poll so the bell doesn't
    // spam the user with every existing unread on mount
    const isFirstLoad = useRef(true);
    // previousUnread: tracks the prior tick's unread count so the closure
    // never sees a stale value. Updated AFTER the comparison in each tick.
    const previousUnread = useRef<number>(0);
    // requestSeq: monotonically increasing id per outbound poll. Stale
    // responses (older than the current seq) are dropped — prevents two
    // in-flight ticks from racing each other when one is slow.
    const requestSeq = useRef<number>(0);
    // openRef: lets the polling tick read the latest `open` state without
    // requiring the effect to re-bind on every toggle.
    const openRef = useRef<boolean>(false);
    openRef.current = open;

    const { toasts, addToast, removeToast } = useToast();

    // Polling loop — uses summary endpoint for the badge tick (cheap),
    // and pulls the full first page only when the dropdown is open OR
    // when summary tells us there are unseen new arrivals.
    useEffect(() => {
        let cancelled = false;

        const fetchFreshList = async (mySeq: number) => {
            try {
                const page = await listNotifications({ page: 1 });
                if (cancelled || mySeq !== requestSeq.current) return;
                const fresh = page.results;
                if (!isFirstLoad.current) {
                    for (const n of fresh) {
                        if (!seenIds.current.has(n.id) && !n.read_at) {
                            addToast(n.display, 'success');
                        }
                    }
                }
                seenIds.current = new Set(fresh.map((n) => n.id));
                isFirstLoad.current = false;
                setItems(fresh);
            } catch (err) {
                console.error('[NotificationsBell] list failed', err);
            }
        };

        const tick = async () => {
            const mySeq = ++requestSeq.current;
            try {
                const summary = await getUnreadSummary();
                if (cancelled || mySeq !== requestSeq.current) return;
                setUnreadCount(summary.total);
                // Pull the list when (a) it's the first tick, (b) the count
                // grew since last tick, or (c) the dropdown is open.
                const grew = summary.total > previousUnread.current;
                previousUnread.current = summary.total;
                if (isFirstLoad.current || grew || openRef.current) {
                    await fetchFreshList(mySeq);
                }
            } catch (err) {
                console.error('[NotificationsBell] summary tick failed', err);
            }
        };

        void tick();
        const id = window.setInterval(tick, POLL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
        // The effect intentionally has no deps. Polling cadence is constant
        // and the loop reads `open` via openRef so it doesn't need to
        // re-bind on every toggle. Bumping requestSeq + openRef is enough
        // to keep behaviour current.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // When the dropdown opens, force a fresh list fetch immediately so the
    // user doesn't have to wait for the next 60s tick.
    useEffect(() => {
        if (!open) return;
        const mySeq = ++requestSeq.current;
        listNotifications({ page: 1 })
            .then((page) => {
                if (mySeq !== requestSeq.current) return;
                seenIds.current = new Set(page.results.map((n) => n.id));
                setItems(page.results);
            })
            .catch((err) => console.error('[NotificationsBell] open-fetch failed', err));
    }, [open]);

    const handleOpen = () => {
        setOpen((v) => !v);
    };

    const handleItemClick = async (n: Notification) => {
        // Optimistic mark-read
        if (!n.read_at) {
            const optimistic = { ...n, read_at: new Date().toISOString() };
            setItems((prev) => prev.map((x) => (x.id === n.id ? optimistic : x)));
            setUnreadCount((c) => Math.max(0, c - 1));
            try {
                await markNotificationRead(n.id);
            } catch (err) {
                // Roll back on failure
                console.error('[NotificationsBell] markRead failed', err);
                setItems((prev) => prev.map((x) => (x.id === n.id ? n : x)));
                setUnreadCount((c) => c + 1);
                return;
            }
        }
        if (n.default_url && onNavigate) {
            onNavigate(n.default_url, n);
            setOpen(false);
        }
    };

    const handleMarkAll = async () => {
        const previous = items;
        const previousUnread = unreadCount;
        const stamp = new Date().toISOString();
        // Optimistic: mark every in-memory row as read immediately.
        setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? stamp })));
        setUnreadCount(0);
        try {
            await markAllNotificationsRead();
            // Pull fresh state from the server so subsequent interactions
            // see the persisted read_at values, not just the optimistic
            // timestamps. Prevents the bell from silently reverting on
            // the next background poll if the backend shape differs.
            try {
                const page = await listNotifications({ page: 1 });
                setItems(page.results);
                setUnreadCount(page.results.filter((n) => !n.read_at).length);
            } catch { /* non-fatal — optimistic state is still correct */ }
        } catch (err) {
            console.error('[NotificationsBell] markAll failed', err);
            setItems(previous);
            setUnreadCount(previousUnread);
        }
    };

    // Close on Escape key or click outside
    const bellRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        const onClick = (e: MouseEvent) => {
            if (bellRef.current && !bellRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onClick);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onClick);
        };
    }, [open]);

    return (
        <div className="notifications-bell" ref={bellRef}>
            <button
                type="button"
                className="icon-btn notifications-bell__btn"
                aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
                aria-expanded={open}
                onClick={handleOpen}
            >
                <Bell size={18} />
                {unreadCount > 0 && (
                    <span className="notifications-bell__badge">{unreadCount}</span>
                )}
            </button>

            {open && (() => {
                // Filter the visible list per active tab. Pure, no side
                // effects — kept inline since this is the only consumer.
                const filtered = items.filter((n) => {
                    if (tab === 'unread') return !n.read_at;
                    if (tab === 'mentions') {
                        const t = n.type.toLowerCase();
                        const payload = (n.payload ?? {}) as Record<string, unknown>;
                        return t.includes('mention') || Boolean(payload.mentioned_user_id);
                    }
                    return true;
                });
                const tabOptions: Array<{ key: 'all' | 'unread' | 'mentions'; label: string; count: number }> = [
                    { key: 'all', label: 'All', count: items.length },
                    { key: 'unread', label: 'Unread', count: unreadCount },
                    { key: 'mentions', label: 'Mentions', count: items.filter((n) => n.type.toLowerCase().includes('mention')).length },
                ];
                return (
                <div
                    className="notifications-bell__panel"
                    role="dialog"
                    aria-label="Notifications"
                >
                    <div className="notifications-bell__panel-header">
                        <h4>Notifications</h4>
                        {unreadCount > 0 && (
                            <button
                                type="button"
                                className="notifications-bell__mark-all"
                                onClick={handleMarkAll}
                            >
                                Mark all read
                            </button>
                        )}
                    </div>

                    <div
                        className="notifications-bell__tabs"
                        role="tablist"
                        aria-label="Notification filters"
                    >
                        {tabOptions.map((opt) => {
                            const active = opt.key === tab;
                            return (
                                <button
                                    key={opt.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    className={`notifications-bell__tab ${active ? 'is-active' : ''}`}
                                    onClick={() => setTab(opt.key)}
                                >
                                    <span>{opt.label}</span>
                                    {opt.count > 0 && (
                                        <span className="notifications-bell__tab-count">
                                            {opt.count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {filtered.length === 0 ? (
                        <p className="notifications-bell__empty">
                            {tab === 'unread'
                                ? "You're all caught up."
                                : tab === 'mentions'
                                    ? 'No mentions yet.'
                                    : 'No notifications.'}
                        </p>
                    ) : (
                        <ul className="notifications-bell__list">
                            {filtered.slice(0, 20).map((n) => (
                                <li
                                    key={n.id}
                                    className={n.read_at ? 'is-read' : 'is-unread'}
                                    data-category={n.category}
                                >
                                    <button
                                        type="button"
                                        className={`notifications-bell__item ${expandedId === n.id ? 'notifications-bell__item--expanded' : ''}`}
                                        onClick={() => {
                                            setExpandedId((prev) => prev === n.id ? null : n.id);
                                            handleItemClick(n);
                                        }}
                                    >
                                        <span className="notifications-bell__item-icon">
                                            <IconFor name={n.icon} size={16} />
                                        </span>
                                        <span className="notifications-bell__item-body">
                                            <span className={`notifications-bell__item-text ${expandedId === n.id ? 'notifications-bell__item-text--expanded' : ''}`}>
                                                {n.display}
                                            </span>
                                            <small>
                                                {new Date(n.created_at).toLocaleString()}
                                            </small>
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                );
            })()}

            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </div>
    );
}

export default NotificationsBell;
